import { incomeTaxYear } from "@stb/jurisdiction-au/income-tax"

import { displayYear } from "../../components/home/home-content"
import { formatAud, SimpleTable, SourceLine } from "../../components/landing"
import { ATO_MEDICARE_CALCULATOR_URL, PayCalculator } from "../../components/landing/pay-calculator"
import { DEFAULT_FY, variantAnswer } from "../../components/landing/pay-calculator-model"
import type { Guide } from "../guide-types"
import { medicareGuide } from "../medicare-levy-calculator"
import { englishDateToZh } from "./dates"
import { PAY_COPY_ZH } from "./pay-calculator-copy"

/**
 * Chinese counterpart of content/medicare-levy-calculator.tsx (T1320). Same engine, thresholds, sources and dates;
 * only the words differ. Terms: docs/design/landing/copy-zh.md.
 */
const year = incomeTaxYear(DEFAULT_FY)
const fy = displayYear(DEFAULT_FY)
const medicare = year.medicare
const checked = englishDateToZh(medicareGuide.checked)
const provisional = medicare.status !== "published"
const previousFy = `${Number(DEFAULT_FY.slice(0, 4)) - 1}–${String(Number(DEFAULT_FY.slice(0, 4))).slice(2)}`

const dollars = (n: number) => `A$${n.toLocaleString("en-AU")}`
const rate = medicare.rateBp / 100
const shadeIn = medicare.shadeInBp / 100
const lower = dollars(medicare.singleLower)
const upper = dollars(medicare.singleUpper)

const t = PAY_COPY_ZH.medicareFigure
const thresholdRows = [
  { header: t.upTo(lower), cells: [t.nil] },
  {
    header: `${dollars(medicare.singleLower + 1)} – ${upper}`,
    cells: [t.shade(`${shadeIn}`, lower)],
  },
  { header: t.over(upper), cells: [t.fullRate(`${rate}%`)] },
]

const linkClass = "underline underline-offset-2"

export const medicareGuideZh: Guide = {
  slug: medicareGuide.slug,
  title: "Medicare levy 计算器",
  description: `按收入算出 ${fy} 财年的 Medicare levy（医疗保险税），含低收入减免。可看每年或每两周的金额，附 ATO 来源，免费使用。`,
  crumb: "Medicare levy 计算器",
  h1: `Medicare levy 计算器（${fy} 财年）`,
  published: medicareGuide.published,
  reviewed: medicareGuide.reviewed,
  badge: "免费工具",
  financialYear: fy,
  checked,
  sources: [
    { label: "ATO，低收入者的 Medicare levy 减免", href: medicareGuide.sources[0].href },
    { label: "ATO Medicare levy 计算器（英文）", href: ATO_MEDICARE_CALCULATOR_URL },
  ],
  answer: variantAnswer("medicare", formatAud, "zh"),
  answerMore: (
    <>
      计算器按 ATO 的门槛，对单身人士计入 {rate}% 的 Medicare levy 和低收入减免，并把 Medicare levy
      与你的所得税、到手收入放在一起显示。家庭、SAPTO（老年人和领养老金者税务抵免）和附加费这里不计算。请在下面填入你自己的收入。
    </>
  ),
  related: [
    { label: "工资计算器：税后到手收入", href: "/pay-calculator" },
    {
      label: "HECS-HELP 还款计算器：你的强制还款额",
      href: "/hecs-repayment-calculator",
    },
    { label: "Cents per km 计算器：算出你的汽车开支扣除额", href: "/cents-per-km-calculator" },
    { label: "个体经营者开发票：发出发票，看谁已付款", href: "/invoicing" },
  ],
  toc: [
    { id: "calculator", label: "算出你的 Medicare levy" },
    { id: "thresholds", label: "低收入门槛" },
    { id: "not-covered", label: "本计算器不涵盖什么" },
    { id: "faq", label: "常见问题" },
  ],
  cta: {
    title: "同时有 ABN 收入？把账放在一处",
    description:
      "Medicare levy 按你全部的应税收入计算，包括 ABN 利润。Kestly 把发票、收据和行程放在一处，报税时已经备好。",
    ctaLabel: "免费开始",
    ctaHref: "/login?from=medicare-levy-calculator",
  },
  body: (
    <>
      <section aria-labelledby="calculator" className="grid gap-4">
        <h2 id="calculator" className="text-2xl font-semibold">
          算出你的 Medicare levy
        </h2>
        <PayCalculator variant="medicare" />
      </section>

      <section aria-labelledby="thresholds" className="grid gap-3">
        <h2 id="thresholds" className="text-2xl font-semibold">
          Medicare levy 低收入门槛
        </h2>
        <p className="max-w-[70ch]">
          Medicare levy 是你应税收入的 {rate}
          %。收入低的人缴得少，或者不用缴。下表是没有受抚养人的单身人士的门槛。
        </p>
        <SimpleTable caption={t.tableCaption(fy)} columns={t.columns} rows={thresholdRows} />
        {provisional && (
          <p className="max-w-[70ch] text-sm">
            ATO 尚未公布 {fy} 财年的门槛。在公布之前，上表和计算器暂定使用 {previousFy} 财年的数值。
          </p>
        )}
        <SourceLine
          sources={[{ label: "ATO", href: medicare.source.url }]}
          financialYear={fy}
          checked={checked}
        />
      </section>

      <section aria-labelledby="not-covered" className="grid max-w-[70ch] gap-3">
        <h2 id="not-covered" className="text-2xl font-semibold">
          本计算器不涵盖什么
        </h2>
        <p>
          家庭、老年人和领养老金者（SAPTO）以及 Medicare levy
          surcharge（附加费）这里不计算。它们的门槛不同，并取决于你的家庭情况和私人医院保险。这些请用{" "}
          <a href={ATO_MEDICARE_CALCULATOR_URL} className={linkClass}>
            ATO Medicare levy 计算器（英文）
          </a>
          。
        </p>
        <p>
          Medicare levy 按全年的应税收入计算。除非你勾选，否则不计入标准扣除额（standard
          deduction），所以如果你有其他扣除额，实际数字可能不同。
        </p>
      </section>
    </>
  ),
  faq: [
    {
      question: "Medicare levy 的税率是多少？",
      answer: `Medicare levy 是你应税收入的 ${rate}%。低收入者缴得少，或者不用缴。`,
    },
    {
      question: "谁不用缴 Medicare levy？",
      answer: `按我们使用的门槛，应税收入不超过 ${lower} 的单身人士不用缴 Medicare levy。有些人因其他原因获得豁免，例如部分外国居民。`,
    },
    {
      question: "低收入减免是怎么运作的？",
      answer: `应税收入在 ${lower} 和 ${upper} 之间时，超出 ${lower} 的部分每 A$1 缴 ${shadeIn} 分，Medicare levy 逐步增加；应税收入超过 ${upper} 后，才缴全额 ${rate}%。`,
    },
    {
      question: "Medicare levy 和私人医疗保险是一回事吗？",
      answer:
        "不是。Medicare levy 用于 Medicare，通过税款缴纳。私人医院保险是另一回事，没有它可能要缴 Medicare levy surcharge（附加费），本计算器不计算附加费。",
    },
    {
      question: "什么是 Medicare levy surcharge？",
      answer:
        "这是对没有合适私人医院保险的高收入者额外征收的费用，取决于你的收入、家庭和保险情况。ATO 的 Medicare levy 计算器可以算出来。",
    },
    {
      question: "家庭的门槛不一样吗？",
      answer:
        "不一样。家庭的门槛更高，并随每个孩子增加；老年人也有自己的门槛。本页只使用单身人士的门槛。",
    },
    {
      question: "外国居民要缴 Medicare levy 吗？",
      answer:
        "一般不用。在“更多选项”里选“外国居民”，就能看到不含 Medicare levy 的结果。打工度假签证持有人（working holiday maker）的处理在 ATO 进一步说明之前属于暂定。",
    },
    {
      question: "Medicare levy 是在所得税之外另外缴的吗？",
      answer: "是。它是加在所得税之上的一笔独立金额，所以到手收入表里两项都会列出。",
    },
    {
      question: "ABN 收入也要缴 Medicare levy 吗？",
      answer:
        "要。Medicare levy 按你全部的应税收入计算，包括 ABN 的利润。ABN 付款不会预扣 Medicare levy，所以要自己留出这笔钱。",
    },
  ],
}
